import {
  Box,
  Stack,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';

import {
  useGetCommunityPosts,
  useCreateCommunityPost,
  useUpdatePost,
  useDeletePost,
  useTogglePostLike,
  useCreateReply,
  useToggleReplyLike,
  useDeleteReply,
  PostResponse,
} from '@/api/endpoints/community/posts';
import { MarkerPin01Icon } from '@/assets/icons/MarkerPin01Icon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import CommunityHubFirstTimeExperience from '@/components/community/CommunityHubFirstTimeExperience';

import { PostCard } from './PostCard';
import { PostForm, PostFormData } from './PostForm';
import { ReplyFormData } from './ReplyForm';

interface CommunityPostsTabProps {
  communityId: number;
  isUserMember: boolean;
  isUserAdmin: boolean;
  onCreatePost?: () => void;
  onEditPost?: (post: PostResponse) => void;
  showCreateForm?: boolean;
  onCloseCreateForm?: () => void;
  onJoinCommunity?: () => void;
}

export const CommunityPostsTab: React.FC<CommunityPostsTabProps> = ({
  communityId,
  isUserMember,
  isUserAdmin,
  onCreatePost,
  onEditPost,
  showCreateForm: externalShowCreateForm,
  onCloseCreateForm,
  onJoinCommunity,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [editingPost, setEditingPost] = useState<PostResponse | null>(null);
  const [internalShowCreateForm, setInternalShowCreateForm] = useState(false);
  const [showLocationAlert, setShowLocationAlert] = useState(false);

  // Use external control if provided, otherwise use internal state
  const showCreateForm =
    externalShowCreateForm !== undefined ? externalShowCreateForm : internalShowCreateForm;
  const setShowCreateForm =
    externalShowCreateForm !== undefined ? onCloseCreateForm : setInternalShowCreateForm;

  const postsPerPage = 10;

  // API Hooks
  const {
    data: posts = [],
    isLoading,
    error,
    refetch,
  } = useGetCommunityPosts({
    communityId,
    page: currentPage,
    limit: postsPerPage,
  });

  // Check if user has a location/property using PropertyManagerProvider
  const { propertyLocations, isFetching: isLoadingUserProperty } = usePropertyManager();
  const userHasLocation = propertyLocations && propertyLocations.length > 0;

  const createPostMutation = useCreateCommunityPost();
  const updatePostMutation = useUpdatePost();
  const deletePostMutation = useDeletePost();
  const toggleLikeMutation = useTogglePostLike();
  const createReplyMutation = useCreateReply();
  const toggleReplyLikeMutation = useToggleReplyLike();
  const deleteReplyMutation = useDeleteReply();

  // Handlers
  const handleCreatePost = async (data: PostFormData) => {
    await createPostMutation.mutateAsync({
      communityId,
      ...data,
    });
    if (setShowCreateForm) {
      setShowCreateForm(false);
    }
    setCurrentPage(1); // Go to first page to see new post
  };

  const handleUpdatePost = async (data: PostFormData) => {
    if (!editingPost) return;

    await updatePostMutation.mutateAsync({
      postId: editingPost.id,
      ...data,
    });
    setEditingPost(null);
  };

  const handleDeletePost = async (postId: number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    await deletePostMutation.mutateAsync({
      postId,
      communityId,
    });
  };

  const handleToggleLike = async (postId: number) => {
    await toggleLikeMutation.mutateAsync({
      postId,
      communityId,
    });
  };

  const handleCreateReply = async (postId: number, data: ReplyFormData) => {
    await createReplyMutation.mutateAsync({
      postId,
      communityId,
      ...data,
    });
  };

  const handleToggleReplyLike = async (replyId: number, postId: number) => {
    await toggleReplyLikeMutation.mutateAsync({
      replyId,
      postId,
      communityId,
    });
  };

  const handleDeleteReply = async (replyId: number, postId: number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this reply?')) {
      return;
    }

    await deleteReplyMutation.mutateAsync({
      replyId,
      postId,
      communityId,
    });
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleCancelCreate = () => {
    if (setShowCreateForm) {
      setShowCreateForm(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPost(null);
  };

  const handleEditPost = (post: PostResponse) => {
    setEditingPost(post);
  };

  const handleCreatePostClick = () => {
    if (!userHasLocation) {
      setShowLocationAlert(true);
      return;
    }
    if (setShowCreateForm) {
      setShowCreateForm(true);
    }
  };

  // Calculate total pages (this would come from API in real implementation)
  const totalPages = Math.max(1, Math.ceil(posts.length / postsPerPage));

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Failed to load posts. Please try again.
      </Alert>
    );
  }

  return (
    <>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.900' }}>
            Community Discussions
          </Typography>

          {isUserMember && (
            <Tooltip
              title={!userHasLocation ? 'Add your location to create posts' : ''}
              placement="bottom"
            >
              <span>
                <Button
                  startIcon={<PlusIcon />}
                  variant="contained"
                  onClick={handleCreatePostClick}
                  disabled={!userHasLocation || isLoadingUserProperty}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'grey.300',
                      color: 'grey.500',
                    },
                  }}
                >
                  Create Post
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Posts List */}
        {!isLoading && (
          <Stack spacing={3}>
            {posts.length === 0 ? (
              <CommunityHubFirstTimeExperience
                onCreatePost={handleCreatePostClick}
                canCreatePost={isUserMember && !!userHasLocation && !isLoadingUserProperty}
                isUserMember={isUserMember}
                onJoinCommunity={onJoinCommunity}
              />
            ) : (
              <>
                {posts.map(post => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onEdit={() => handleEditPost(post)}
                    onDelete={() => handleDeletePost(post.id)}
                    onToggleLike={() => handleToggleLike(post.id)}
                    onCreateReply={data => handleCreateReply(post.id, data)}
                    onToggleReplyLike={replyId => handleToggleReplyLike(replyId, post.id)}
                    onDeleteReply={replyId => handleDeleteReply(replyId, post.id)}
                    showActions={isUserMember}
                    isUserMember={isUserMember}
                    isUserAdmin={isUserAdmin}
                    createReplyLoading={createReplyMutation.isPending}
                  />
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Stack>
        )}

        {/* Member-only message */}
        {!isUserMember && posts.length > 0 && (
          <Alert severity="info" sx={{ mt: 3 }}>
            Join this community to create posts, reply to discussions, and interact with other
            members.
          </Alert>
        )}

        {/* Location required message */}
        {isUserMember && !userHasLocation && !isLoadingUserProperty && (
          <Alert
            severity="warning"
            sx={{ mt: 3 }}
            action={
              <Button size="small" href="/property-manager/property-locations/create">
                Add Location
              </Button>
            }
          >
            You need to add your location before you can create posts in the community hub.
          </Alert>
        )}
      </Stack>

      {/* Create Post Dialog */}
      <Dialog
        open={showCreateForm}
        onClose={handleCancelCreate}
        maxWidth="md"
        fullWidth
        keepMounted={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.5rem',
            color: 'secondary.900',
            pb: 1,
          }}
        >
          Create New Post
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <PostForm
            onSubmit={handleCreatePost}
            onCancel={handleCancelCreate}
            isLoading={createPostMutation.isPending}
            error={createPostMutation.error?.message}
            mode="create"
            showPinOption={isUserAdmin}
            title="Create New Post"
          />
        </DialogContent>
      </Dialog>

      {/* Edit Post Dialog */}
      <Dialog
        open={!!editingPost}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
        keepMounted={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.5rem',
            color: 'secondary.900',
            pb: 1,
          }}
        >
          Edit Post
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          {editingPost && (
            <PostForm
              onSubmit={handleUpdatePost}
              onCancel={handleCancelEdit}
              initialData={editingPost}
              isLoading={updatePostMutation.isPending}
              error={updatePostMutation.error?.message}
              mode="edit"
              showPinOption={isUserAdmin}
              title="Edit Post"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Location Alert Dialog */}
      <Dialog
        open={showLocationAlert}
        onClose={() => setShowLocationAlert(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.25rem',
            color: 'secondary.900',
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MarkerPin01Icon width={24} height={24} />
          Location Required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            To create posts in the community hub, you need to add your location first. This helps us
            connect you with neighbors in your area.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => setShowLocationAlert(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              href="/dashboard"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              Add Location
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};
